/*
  Send wishlist clicks to the wishlist PAGE instead of the popup.

  Wishlist Hero decides this from its own setting, DisplayWishlistAs, which is
  "popup_window" here. The page it would use in "seprate_page" mode already
  exists and works: /a/wishlist-hero/wishlist, its app proxy.

  We do not touch the app's setting — that is its data, and the app would
  overwrite it on its next save. Instead we intercept the click in the CAPTURE
  phase, before the app's own bubble-phase handler runs, and navigate to that
  page. Keeping the href as #hero-wishlist matters: the app recognises that
  anchor and injects the live item count into the link, which we want to keep.

  If the owner later flips the setting in the app, this becomes a harmless
  no-op that reaches the same URL a moment earlier.
*/
(function () {
  'use strict';

  var WISHLIST_PAGE = '/a/wishlist-hero/wishlist';
  var SELECTOR = 'a[href="#hero-wishlist"], a[href$="#hero-wishlist"], .wishlist-hero-header-icon';

  function pageUrl() {
    var root = '/';
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        root = window.Shopify.routes.root;
      }
    } catch (e) {}
    return root.replace(/\/$/, '') + WISHLIST_PAGE;
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    // Let modified clicks (new tab, download, middle-click) behave normally.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var trigger = event.target.closest ? event.target.closest(SELECTOR) : null;
    if (!trigger) return;
    // Already on the wishlist page: nothing to do.
    if (window.location.pathname === pageUrl()) { event.preventDefault(); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.href = pageUrl();
  }, true);
})();

/* ---------------------------------------------------------------------------
   Dashboard chrome for the wishlist page.

   The page at /a/wishlist-hero/wishlist is Wishlist Hero's app proxy: the theme
   supplies the layout, the app supplies everything inside #__next. We cannot
   author the app's markup, and its public SDK is paywalled on this plan, so the
   cards, counts and bulk buttons stay the app's. What we CAN do is put that
   content inside the same account layout as the dashboard, add the breadcrumb,
   the subtitle and the page heading, and restyle the rest with CSS.

   The sidebar is rendered by Liquid into #wishlist-account-chrome (see
   sections/header.liquid) rather than built here, so its links, the order count
   and the signed-in state are all real.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  function onWishlistPage() {
    return /\/a\/wishlist-hero\/wishlist\/?$/.test(window.location.pathname);
  }

  function build() {
    var appRoot = document.getElementById('__next');
    var tpl = document.getElementById('wishlist-account-chrome');
    if (!appRoot || appRoot.dataset.accountWrapped) return;
    // Without the template the customer is signed out; leave the app alone.
    if (!tpl) return;

    appRoot.dataset.accountWrapped = 'true';

    var page = document.createElement('div');
    page.className = 'account-page account-page--wishlist';

    var grid = document.createElement('div');
    grid.className = 'account-page__grid page-width';

    grid.appendChild(tpl.content.cloneNode(true));

    var main = document.createElement('div');
    main.className = 'account-main';

    var head = document.createElement('header');
    head.className = 'account-head';
    head.innerHTML =
      '<div class="account-head__text">' +
        '<span class="account-head__eyebrow">My Account</span>' +
        '<h1 class="account-head__title">My Wishlist</h1>' +
        '<p class="account-head__sub">Your saved products, all in one place.</p>' +
      '</div>';

    main.appendChild(head);
    appRoot.parentNode.insertBefore(page, appRoot);
    main.appendChild(appRoot);
    grid.appendChild(main);
    page.appendChild(grid);

    document.documentElement.classList.add('wishlist-page-styled');
  }

  function start() {
    if (!onWishlistPage()) return;
    build();
    // The app mounts asynchronously; re-check until its root exists.
    var tries = 0;
    var t = setInterval(function () {
      build();
      if (++tries > 40 || document.querySelector('[data-account-wrapped]')) clearInterval(t);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ---------------------------------------------------------------------------
   Item count in the bulk bar.

   Counted from the cards the app has rendered, not invented and not cached:
   the SDK that would report a count returns 403 on this plan. A MutationObserver
   keeps it honest when items are removed, since the app re-renders in place.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (!/\/a\/wishlist-hero\/wishlist\/?$/.test(window.location.pathname)) return;

  function label(n) { return n + (n === 1 ? ' item' : ' items'); }

  function sync() {
    var listing = document.querySelector('.wishlisthero-customer-view-listing');
    var bar = document.querySelector('.wishlist-hero-list-footer');
    if (!listing || !bar) return;
    var n = listing.querySelectorAll('.wishlist-hero-list-card').length;
    var el = bar.querySelector('.wishlist-hero-count');
    if (!el) {
      el = document.createElement('p');
      el.className = 'wishlist-hero-count';
      el.setAttribute('aria-live', 'polite');
      bar.insertBefore(el, bar.firstChild);
    }
    if (el.textContent !== label(n)) el.textContent = label(n);
  }

  function watch() {
    var root = document.getElementById('__next');
    if (!root) return false;
    sync();
    new MutationObserver(sync).observe(root, { childList: true, subtree: true });
    return true;
  }

  var tries = 0;
  var t = setInterval(function () {
    if (watch() || ++tries > 40) clearInterval(t);
  }, 250);
})();

/* ---------------------------------------------------------------------------
   Button label casing.

   Wishlist Hero stores its button text in SHOUTING CAPS ("ADD TO CART",
   "REMOVE ALL FROM WISHLIST"). That is the app's stored configuration, not a
   text-transform, so CSS cannot undo it, and the settings screen that owns
   those strings is a cross-origin frame this theme cannot reach.

   So relabel in the DOM: exact, whole-string matches only, so a product name is
   never touched, and only the text node is rewritten so icons survive. A
   MutationObserver re-applies it because the app re-renders on every change.
   If the owner ever edits the strings in the app, the map simply stops
   matching and their wording wins.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  var LABELS = {
    'ADD TO CART': 'Add to cart',
    'ADD ALL TO CART': 'Add all to cart',
    'REMOVE ALL FROM WISHLIST': 'Remove all',
    'ADDED TO WISHLIST': 'Added to wishlist',
    'ADD TO WISHLIST': 'Add to wishlist',
    'send email': 'Send email',
    'SEND EMAIL': 'Send email',
    'VIEW CART': 'View cart',
    'VIEW WISHLIST': 'View wishlist',
    'there are no items in this wishlist': 'Your wishlist is empty'
  };

  function relabelNode(node) {
    var raw = node.nodeValue;
    if (!raw) return;
    var key = raw.trim();
    if (!key || !Object.prototype.hasOwnProperty.call(LABELS, key)) return;
    var replacement = raw.replace(key, LABELS[key]);
    if (node.nodeValue !== replacement) node.nodeValue = replacement;
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    var labels = root.querySelectorAll('.MuiButton-label, .wishlisthero-button-text, .wishlist-hero-list-no-items-text');
    Array.prototype.forEach.call(labels, function (el) {
      Array.prototype.forEach.call(el.childNodes, function (n) {
        if (n.nodeType === 3) relabelNode(n);
      });
    });
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; scan(document.body); });
  }

  function start() {
    scan(document.body);
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ---------------------------------------------------------------------------
   Empty wishlist state.

   The app renders one line of text in .wishlist-hero-list-no-items-content.
   The heading itself is reworded by the label map above; this adds the
   supporting line and a real link into the catalogue, then CSS turns the block
   into the dashed panel from the design. Nothing here fabricates data: it only
   appears when the app says the list is empty.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (!/\/a\/wishlist-hero\/wishlist\/?$/.test(window.location.pathname)) return;

  /* Our two nodes are owned outright and removed when items exist. Hiding the
     app's empty-state box is not enough: React re-parents and re-renders that
     block, which orphaned the injected nodes above the product grid. */
  function enhance() {
    var hasItems = document.querySelectorAll('.wishlist-hero-list-card').length > 0;
    var mine = document.querySelectorAll('[data-wishlist-empty-extra]');

    if (hasItems) {
      Array.prototype.forEach.call(mine, function (n) { n.remove(); });
      return;
    }

    var box = document.querySelector('.wishlist-hero-list-no-items-content');
    if (!box) return;
    if (box.querySelector('[data-wishlist-empty-extra]')) return;
    Array.prototype.forEach.call(mine, function (n) { n.remove(); });

    var sub = document.createElement('p');
    sub.className = 'wishlist-empty__sub';
    sub.setAttribute('data-wishlist-empty-extra', '');
    sub.textContent = 'Save your favorite products and come back to them later.';

    var cta = document.createElement('a');
    cta.className = 'btn-blue wishlist-empty__cta';
    cta.setAttribute('data-wishlist-empty-extra', '');
    cta.href = '/collections/all';
    cta.textContent = 'Start shopping';

    box.appendChild(sub);
    box.appendChild(cta);
  }

  var tries = 0;
  var t = setInterval(function () {
    enhance();
    if (++tries > 40) clearInterval(t);
  }, 250);
  new MutationObserver(enhance).observe(document.documentElement, { childList: true, subtree: true });
})();
